import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select
} from '@mui/material'
import { FC, useCallback, useMemo } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  AnswerToCannedJobWithRelated,
  EntryWithRelated,
  MultipleChoiceAnswerWithRelated
} from '../../../models/inspections'
import { useUpdateAnswerToCannedJobMutation } from '../../../redux/api/inspectionApi'
import {
  CannedJobExpanded,
  InspectionAnswerToCannedJobEntry
} from '../../../sdk'

interface Props {
  answers: MultipleChoiceAnswerWithRelated[]
  entry: EntryWithRelated
  setEntry: (entry: EntryWithRelated) => void
  open: boolean
  setOpen: (open: boolean) => void
}
type CannedJobWithAnswer = {
  cannedJob: CannedJobExpanded
  answerToCannedJob: InspectionAnswerToCannedJobEntry[]
}

function transformAnswerToCannedJobs(
  answerToCannedJobs: AnswerToCannedJobWithRelated[]
) {
  const res: { [key: string]: CannedJobWithAnswer } = {}
  for (const answerToCannedJob of answerToCannedJobs) {
    const { cannedJobExpanded, ...entry } = answerToCannedJob
    if (cannedJobExpanded.entry!.id! in res) {
      res[cannedJobExpanded.entry!.id!] = {
        cannedJob: cannedJobExpanded,
        answerToCannedJob: [
          ...res[cannedJobExpanded.entry!.id!].answerToCannedJob,
          { ...entry }
        ]
      }
    } else {
      res[cannedJobExpanded.entry!.id!] = {
        cannedJob: cannedJobExpanded,
        answerToCannedJob: [{ ...entry }]
      }
    }
  }
  return Object.values(res)
}

export const ActionSelect: FC<Props> = ({
  answers,
  entry,
  setEntry,
  open,
  setOpen
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const [update] = useUpdateAnswerToCannedJobMutation()

  const cannedJobs = useMemo(
    () =>
      transformAnswerToCannedJobs(
        answers.flatMap((answer) => answer.answerToCannedJobs || [])
      ),
    [answers]
  )
  const selected = useMemo(
    () =>
      cannedJobs.filter((cannedJob) =>
        cannedJob.answerToCannedJob.some(
          (answerToCannedJob) => answerToCannedJob.info?.selected
        )
      ),
    [cannedJobs]
  )

  const handleChange = useCallback(
    (
      checked: boolean,
      answerToCannedJobs: InspectionAnswerToCannedJobEntry[]
    ) => {
      // This object will hold the id and the checked status of each answerToCannedJob
      const answerToCannedJobIdToSelected: { [key: string]: boolean } = {}
      for (const answerToCannedJob of answerToCannedJobs) {
        answerToCannedJobIdToSelected[answerToCannedJob.id!] = checked
      }
      const updatedAnswers: MultipleChoiceAnswerWithRelated[] = []

      entry.prompts[0].answers.forEach((answer) => {
        const updatedAnswerToCannedJobs: AnswerToCannedJobWithRelated[] = []
        answer.answerToCannedJobs?.forEach((a) => {
          if (a.id! in answerToCannedJobIdToSelected) {
            const updated = {
              ...a,
              info: {
                ...a.info!,
                selected: answerToCannedJobIdToSelected[a.id!]
              }
            }
            updatedAnswerToCannedJobs.push(updated)
          } else {
            updatedAnswerToCannedJobs.push(a)
          }
        })
        updatedAnswers.push({
          ...answer,
          answerToCannedJobs: updatedAnswerToCannedJobs
        })
      })
      // update UI before sending request
      setEntry({
        ...entry,
        prompts: [{ ...entry.prompts[0], answers: updatedAnswers }]
      })
      // sending request
      for (const answerToCannedJob of answerToCannedJobs) {
        update({
          authorization: accessToken,
          inspectionAnswerToCannedJobEntry: {
            ...answerToCannedJob,
            info: {
              ...answerToCannedJob.info!,
              selected: checked
            }
          }
        })
      }
    },
    [accessToken, entry, setEntry, update]
  )

  if (cannedJobs.length === 0) {
    return <></>
  }

  return (
    <FormControl variant="standard">
      <InputLabel id="multi-answer-label">Actions</InputLabel>
      <Select
        labelId="multi-answer-label"
        id="multi-answer"
        multiple
        value={selected.map((cannedJob) => cannedJob.cannedJob.entry!.id!)}
        renderValue={() =>
          selected
            .map((cannedJob) => cannedJob.cannedJob!.entry!.info!.name)
            .join(', ')
        }
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}>
        {cannedJobs.map((cannedJob) => (
          <MenuItem
            key={cannedJob.cannedJob.entry!.id!}
            value={cannedJob.cannedJob.entry!.id!}>
            <Checkbox
              checked={
                selected
                  .map((cannedJob) => cannedJob.cannedJob.entry!.id!)
                  .indexOf(cannedJob.cannedJob.entry!.id!) > -1
              }
              onChange={(event) =>
                handleChange(event.target.checked, cannedJob.answerToCannedJob)
              }
            />
            <ListItemText primary={cannedJob.cannedJob!.entry!.info!.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
